<p-card>
  <ng-template pTemplate="title">
    <div class="tw:flex tw:items-center tw:justify-contents-between">
      Job History
      @if (object() === OBJECTS.MONITORING) {
        <div class="tw:ms-auto tw:flex tw:items-center">
          <cor-execution-options
            [id]="id()"
            [object]="object()" />
        </div>
      }
    </div>
  </ng-template>
  <div class="tw:pt-0">
    <div class="tw:mb-4">
      <div class="row tw:gap-2">
        <div class="col-12 tw:mt-2">
          @if (object() === OBJECTS.PROMPT) {
            <p-message
              severity="secondary"
              [closable]="false">
              Independent jobs on a prompt can be seen here, but Prompts tested with Models or RAGs
              can be accessed in their respective pages
            </p-message>
          } @else if (object() === OBJECTS.RAG) {
            <p-message
              severity="secondary"
              icon="pi pi-info-circle"
              [closable]="false">
              Independent jobs on a RAG can be seen here, but RAGs tested with Models can be
              accessed in the Model's page
            </p-message>
          }
        </div>
        <!-- Pinned jobs -->

        @if (pinnedJob.isLoading()) {
          <cor-skeleton [type]="SKELETON_TYPES.TEXT" />
        } @else {
          @if (pinnedJob.list().length > 0) {
            <span>Pinned Jobs:</span>
            <div class="tw:grid tw:grid-cols-12 tw:gap-2 tw:mb-3">
              @for (job of pinnedJob.list(); track job.id) {
                <div
                  class="tw:cursor-pointer tw:flex tw:min-w-0 tw:items-center tw:gap-3 tw:px-3 tw:py-2 tw:border tw:border-surface tw:hover:bg-emphasis tw:rounded-sm tw:md:col-span-6 tw:col-span-12"
                  [attr.test-id]="'pinned-job-' + job.name"
                  (click)="navigateToJob(job)">
                  <div class="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
                    <div class="tw:font-semibold tw:truncate tw:text-color">
                      {{ job.name }}
                    </div>
                    <small class="tw:truncate tw:text-muted-color-emphasis">
                      created by
                      <strong>{{ job.createdBy }}</strong>
                      ,
                      <span [title]="job.createdDate | localDate">
                        {{ job.createdDate | dateFromNow }}
                      </span>
                    </small>
                  </div>
                  <div class="tw:flex tw:shrink-0 tw:items-center tw:gap-2">
                    <cor-status-icon [status]="job.jobStatus" />
                    <div [ngStyle]="{ cursor: 'default' }">
                      <cor-job-runtime-info
                        [jobSteps]="job.jobSteps"
                        (showEvent)="toggleRuntimeInfo($event)"
                        (hideEvent)="toggleRuntimeInfo($event)"
                        (click)="$event.stopPropagation()" />
                    </div>

                    <i
                      [ngStyle]="{ cursor: 'default' }"
                      class="pi pi-thumbtack icon-warn"
                      pTooltip="This is a pinned simulation"></i>
                  </div>
                </div>
              }
            </div>
          }
        }
        <div class="tw:flex tw:justify-between tw:items-center tw:flex-wrap">
          <p-selectButton
            test-id="job-history-users-filter-form-control"
            [formControl]="usersFilterFormControl"
            [unselectable]="true"
            [options]="userFilterItems"
            optionLabel="text"
            optionValue="value" />

          <p-button
            [buttonProps]="{ autofocus: false }"
            (click)="getSimulations()"
            [disabled]="isLoading()"
            [loading]="isLoading()"
            severity="secondary"
            [label]="
              isLoading()
                ? 'Refreshing ...'
                : lastUpdatedTime()
                  ? 'Last updated at ' + (lastUpdatedTime() | date: 'h:mm:ss a')
                  : 'Refresh'
            "
            icon="pi pi-refresh"
            test-id="job-history-refresh-button" />
        </div>
      </div>
    </div>

    <!-- NOTE: Loading happens periodically or when user hits refresh. Show skeleton only on the first load -->
    @if (isLoading() && !lastUpdatedTime()) {
      <cor-skeleton [type]="SKELETON_TYPES.JOB_LIST" />
    } @else {
      <dx-data-grid
        #jobsList
        [dataSource]="jobList()"
        [wordWrapEnabled]="true"
        [showRowLines]="true"
        [showBorders]="true"
        [showColumnLines]="false"
        [hoverStateEnabled]="true"
        [allowColumnResizing]="true"
        [allowColumnReordering]="true"
        [columnHidingEnabled]="true"
        [columnAutoWidth]="true"
        columnResizingMode="widget"
        [elementAttr]="{ class: 'resizable-columns data-grid-row-clickable' }"
        (onRowClick)="onJobClick($event)"
        (onContextMenuPreparing)="onContextMenuPreparing($event)"
        noDataText="{{
          usersFilterFormControl.value === USER_FILTER.MY_JOBS
            ? 'Try running a simulation from the \'Run\' button above'
            : usersFilterFormControl.value === USER_FILTER.ALL_JOBS
              ? 'No jobs found run by you or other users'
              : usersFilterFormControl.value === USER_FILTER.VERIFIED_JOBS
                ? 'Jobs used during approval will show up here. First, Run a job anywhere that uses this object and then, use that job during approval here'
                : 'No jobs present'
        }}">
        <!-- Configurations -->
        <dxo-data-grid-paging [pageSize]="pageSize()" />
        <dxo-data-grid-load-panel [enabled]="true" />
        <dxo-data-grid-pager
          [showPageSizeSelector]="true"
          [allowedPageSizes]="[10, 20, 50]"
          [showNavigationButtons]="true"
          [showInfo]="true" />
        <dxo-data-grid-header-filter
          [visible]="true"
          height="50vh"
          width="100%"
          [search]="{ enabled: true }" />
        <!-- Columns -->
        <!-- Column: Job Type -->
        <dxi-data-grid-column
          caption="Job Type"
          dataField="executionType"
          width="10%"
          dataType="string" />
        <!-- Column: CreatedDate -->
        <dxi-data-grid-column
          caption="Created Date"
          dataField="createdDate"
          width="15%"
          dataType="string"
          cellTemplate="datetimeTemplate" />
        <!-- Column: CreatedBy -->
        <dxi-data-grid-column
          caption="User"
          dataField="createdBy"
          dataType="string"
          width="15%"
          cellTemplate="avatarTemplate" />
        <!-- Column: SubmissionDate -->
        @if (object() === OBJECTS.MONITORING) {
          <!-- FIXME: Ideally, we should use dataType="date" which formats the header-filter correctly.
            But unable to get that working with the right moment commands. So, using dataType=string for now. -->
          <dxi-data-grid-column
            caption="Submission Date"
            dataField="descriptionSectionData.submissionDate"
            width="15%"
            dataType="string"
            cellTemplate="dateTemplate" />
        }
        <!-- Column: JobStatus -->
        <dxi-data-grid-column
          caption="Status"
          dataField="jobStatus"
          width="15%"
          dataType="string"
          cellTemplate="status" />
        <!-- Column: Name -->
        <dxi-data-grid-column
          caption="Name"
          dataField="name"
          width="20%"
          dataType="string"
          cellTemplate="name" />
        <!-- Column: Description -->
        <dxi-data-grid-column
          [allowSorting]="false"
          caption="Description"
          dataField="descriptionSectionData.description"
          dataType="string"
          width="20%"
          cellTemplate="description" />
        <!-- Column: Actions -->
        <dxi-data-grid-column
          width="5%"
          cellTemplate="actionButtonTemplate" />
        <!-- Template: date -->
        <div *dxTemplate="let row of 'datetimeTemplate'">
          {{ row.value | localDate }}
        </div>
        <!-- Template: Submission Date -->
        <div *dxTemplate="let row of 'dateTemplate'">
          {{ row.value | date: 'MM-dd-yyyy' }}
        </div>

        <!-- name template -->
        <div *dxTemplate="let row of 'name'">
          <span class="tw:flex tw:items-center tw:gap-2">
            {{ row.value }}
            @if (row.data.isVerified) {
              <p-tag
                severity="success"
                icon="pi pi-check-circle"
                class="tw:py-0"
                value="verified" />
            }
            @if (row.data.isOld) {
              <p-tag
                value="old"
                class="tw:py-0 tw:leading-relaxed"
                severity="warn"
                pTooltip="The assets used for this job have been modified." />
            }
          </span>
        </div>

        <!-- description template -->
        <div
          class="tw:truncate"
          *dxTemplate="let row of 'description'">
          <span>
            {{ row.value }}
          </span>
        </div>

        <!-- CreatedBy template -->
        <div *dxTemplate="let row of 'avatarTemplate'">
          <div class="tw:flex tw:items-center tw:gap-2">
            <p-avatar
              class="tw:shrink-0 tw:w-[1.5rem] tw:h-[1.5rem] tw:text-xs"
              [label]="row.value | nameBadge"
              [ngStyle]="{
                backgroundColor: (row.value | uniqueRgbColor),
                color: 'white',
              }"
              shape="circle" />
            <span>
              {{ row.value }}
            </span>
          </div>
        </div>

        <!-- status template -->
        <div
          class="tw:flex tw:flex-row tw:gap-1"
          *dxTemplate="let row of 'status'">
          <cor-status-icon [status]="row.data.jobStatus" />
          @if (row.data.hasErrors) {
            <p-button
              test-id="job-history-logs"
              [buttonProps]="{ autofocus: false }"
              (click)="navigateToJob(row.data, true)"
              variant="text"
              severity="primary"
              size="small"
              label="Logs"
              styleClass="tw:py-0" />
          }
          <div style="cursor: default">
            <cor-job-runtime-info
              [jobSteps]="row.data.jobSteps"
              (showEvent)="toggleRuntimeInfo($event)"
              (hideEvent)="toggleRuntimeInfo($event)"
              (click)="$event.stopPropagation()" />
          </div>
        </div>
        <!-- action buttons template -->
        <div *dxTemplate="let row of 'actionButtonTemplate'">
          @if (row.data.actionList.length > 0) {
            <p-button
              test-id="job-history-action-menu-toggle"
              [buttonProps]="{ autofocus: false }"
              type="button"
              (click)="$event.stopPropagation(); actionMenu.toggle($event)"
              rounded
              severity="secondary"
              variant="text"
              icon="pi pi-ellipsis-v" />
            <p-menu
              #actionMenu
              [model]="row.data.actionList"
              appendTo="body"
              [popup]="true"
              [style]="{ width: '15rem' }" />
          }
        </div>
      </dx-data-grid>
    }

    <!-- Edit simulation data popup::START -->
    <!-- As per devex documentation, dynamically return height of the popup when inner elements are added/removed/changed -->

    <cor-dialog-box
      [(visible)]="editJob.isEditModalOpen"
      [title]="`Edit ${editJob.job()?.executionType} Information`">
      @if (editJob.editJobForm) {
        <section>
          <form
            [formGroup]="editJob.editJobForm"
            (ngSubmit)="onEditJobSubmit()">
            <div class="row">
              <div class="col-12">
                <!-- Simulation name -->
                <cor-input-container
                  label="Name"
                  test-id="name">
                  <input
                    test-id="job-history-name"
                    pInputText
                    type="text"
                    [fluid]="true"
                    placeholder="Enter a name for the job"
                    [formControl]="editJob.editJobForm.controls.name" />
                </cor-input-container>
                <!-- Description -->
                <cor-input-container
                  label="Description"
                  test-id="description">
                  <textarea
                    test-id="job-history-description"
                    pTextarea
                    placeholder="Enter a description for the job"
                    type="text"
                    [ngStyle]="{
                      'min-height': '70px',
                      'field-sizing': 'content',
                    }"
                    [formControl]="editJob.editJobForm.controls.description"></textarea>
                </cor-input-container>
                @if (editJob.job()?.executionType === EXECUTION_TYPE.MONITORING) {
                  <cor-input-container
                    label="Submission Date"
                    test-id="submission-date">
                    <p-datepicker
                      test-id="job-history-submission-date"
                      placeholder="Select Submission Date for the Monitoring Task"
                      [formControl]="editJob.editJobForm.controls.submissionDate"
                      dateFormat="mm-dd-yy"
                      [showClear]="true"
                      [showIcon]="true"
                      iconDisplay="input"
                      appendTo="body" />
                  </cor-input-container>
                }
              </div>
            </div>
          </form>
        </section>
      }

      <ng-template #footer>
        <div class="tw:flex tw:gap-2">
          <p-button
            test-id="job-history-cancel-changes"
            [buttonProps]="{ autofocus: false }"
            (click)="closeEditJob()"
            severity="secondary"
            label="Cancel Changes" />
          <p-button
            test-id="job-history-save-changes"
            [buttonProps]="{ autofocus: false }"
            [loading]="editJob.isSaving()"
            [disabled]="editJob.isSaving()"
            (click)="onEditJobSubmit()"
            [label]="editJob.isSaving() ? 'Saving...' : 'Save Changes'" />
        </div>
      </ng-template>
    </cor-dialog-box>
    <!-- Edit simulation data popup::END -->

    <cor-dialog-box
      [(visible)]="removeOldLabelDialog.visible"
      title='Remove "old" label'
      [style]="{ width: '36rem', 'max-width': '95vw' }">
      <cor-input-container label="Comment (optional)">
        <textarea
          pTextarea
          class="tw:w-full"
          rows="4"
          maxlength="4000"
          [formControl]="removeOldLabelDialog.commentControl"
          placeholder="Explain why the existing results can still be used"></textarea>
      </cor-input-container>

      <ng-template #footer>
        <div class="tw:flex tw:justify-end tw:gap-2">
          <p-button
            severity="secondary"
            variant="text"
            label="Cancel"
            (click)="removeOldLabelDialog.visible.set(false)" />
          <p-button
            label="Submit"
            (click)="removeOldLabelDialog.submit()" />
        </div>
      </ng-template>
    </cor-dialog-box>
  </div>
</p-card>
